/* eslint-disable */

import React, { useMemo, useState } from 'react'
import { Box, Typography, Grid, CircularProgress, Pagination } from '@mui/material'
import YarnGroupCard from './YarnGroupCard'

interface Yarn {
  id: number
  name: string
  brand: string
}

interface Colorway {
  id: number
  name: string
  hex_color: string
  hex_colors?: string[]
  yarn?: Yarn
}

interface UserYarn {
  id: number
  colorway_id: number
  dye_lot: string
  quantity: number
  colorway?: Colorway
}

interface PaginationData {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface YarnLibraryProps {
  userYarns: UserYarn[]
  loading: boolean
  onDelete: (id: number) => void
  onUpdate?: (id: number, quantity: number) => Promise<void>
  onAddDyeLot?: (colorwayId: number, dyeLot: string, quantity: number) => Promise<void>
  onAddColorway?: (yarnId: number) => void
  pagination?: PaginationData
  onPageChange?: (page: number) => void
  searchQuery?: string
}

export default function YarnLibrary({ userYarns, loading, onDelete, onUpdate, onAddDyeLot, onAddColorway, pagination, onPageChange, searchQuery }: YarnLibraryProps) {
  const [openCardYarnId, setOpenCardYarnId] = useState<number | null>(null)

  // Group user_yarns by yarn (brand + name combination)
  const groupedYarns = useMemo(() => {
    const groups = new Map<number, { yarn: Yarn; userYarns: UserYarn[] }>()
    
    userYarns.forEach((userYarn) => {
      if (userYarn.colorway && userYarn.colorway.yarn) {
        const yarnId = userYarn.colorway.yarn.id
        if (!groups.has(yarnId)) {
          groups.set(yarnId, {
            yarn: userYarn.colorway.yarn,
            userYarns: []
          })
        }
        groups.get(yarnId)!.userYarns.push(userYarn)
      }
    })
    
    return Array.from(groups.values())
  }, [userYarns])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (userYarns.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {searchQuery ? (
            `No yarns found matching "${searchQuery}". Try a different search term.`
          ) : (
            'Your yarn library is empty. Click "Add Yarn" to get started!'
          )}
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Grid container spacing={2}>
        {groupedYarns.map((group) => (
          <Grid item xs={12} sm={6} md={4} key={group.yarn.id}>
            <YarnGroupCard 
              yarn={group.yarn} 
              userYarns={group.userYarns}
              onDelete={onDelete} 
              onUpdate={onUpdate}
              onAddDyeLot={onAddDyeLot}
              onAddColorway={onAddColorway ? (yarnId) => {
                setOpenCardYarnId(null)
                onAddColorway(yarnId)
              } : undefined}
              open={openCardYarnId === group.yarn.id}
              onOpen={() => setOpenCardYarnId(group.yarn.id)}
              onClose={() => setOpenCardYarnId(null)}
            />
          </Grid>
        ))}
      </Grid>
      
      {pagination && pagination.total_pages > 1 && onPageChange && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={pagination.total_pages}
            page={pagination.current_page}
            onChange={(event, page) => onPageChange(page)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  )
}
